import LText from "@components/LText";
import { useAlbums } from "@hooks/trpc/useAlbums";
import { usePlaylist } from "@hooks/trpc/usePlaylists";
import { useScrollValue } from "@components/AnimatedStore";
import Animated, {
  interpolate,
  useAnimatedStyle,
  useDerivedValue,
} from "react-native-reanimated";
import { useArtistProfile } from "@hooks/trpc/useArtists";
import { Platform } from "react-native";
import { useNavigationStore } from "@utils/navigation/useNavigationStore";
import { useShallow } from "zustand/react/shallow";
import { useMemo } from "react";

export const CollectionTitle = () => {
  const { currentPath, artistId, collectionType, collectionId } =
    useNavigationStore(
      useShallow((state) => ({
        currentPath: state.currentPath,
        artistId: state.artistId,
        collectionType: state.collectionType,
        collectionId: state.collectionId,
      })),
    );

  const scrollValue = useScrollValue();

  // A style that returns a transform counts as changed on every run, and each
  // change commits a layout pass on the UI thread. Deriving the clamped
  // progress first stops the style running outside the fade range.
  const progress = useDerivedValue(() =>
    interpolate(scrollValue.value, [240, 280], [0, 1], "clamp"),
  );

  const viewStyle = useAnimatedStyle(() => {
    return {
      opacity: progress.value,
      transform: [{ translateY: 10 * (1 - progress.value) }],
    };
  });

  // Determine which title component to render based on pathname
  const titleComponent = useMemo(() => {
    // Artist page: /artist/[artistId]
    if (currentPath.includes("/artist/") && artistId) {
      return <ArtistTitle artistId={artistId} />;
    }

    // Collection pages: /[collectionType]/[collectionId]
    if (!collectionType || !collectionId) {
      return null;
    }

    switch (collectionType) {
      case "album":
        return <AlbumTitle collectionId={collectionId} />;
      case "playlist": {
        const isPersonalPlaylist =
          collectionId.startsWith("liked_") ||
          collectionId === "discover_daily";
        return isPersonalPlaylist ? (
          <PersonalPlaylistTitle collectionId={collectionId} />
        ) : (
          <PlaylistTitle collectionId={collectionId} />
        );
      }
      case "artist":
        return null;
    }
  }, [currentPath, artistId, collectionType, collectionId]);

  if (!titleComponent) return null;

  return (
    <Animated.View
      style={[
        viewStyle,
        {
          width: Platform.OS === "web" ? "100%" : undefined,
        },
      ]}
    >
      {titleComponent}
    </Animated.View>
  );
};

const AlbumTitle = ({ collectionId }: { collectionId: string }) => {
  const {
    query: { data },
  } = useAlbums([collectionId]);
  const album = data?.at(0);

  return (
    <LText
      fontWeight={600}
      fontSize={16}
      numberOfLines={1}
      style={{ textAlign: Platform.OS === "web" ? "center" : undefined }}
    >
      {album?.title}
    </LText>
  );
};

interface ArtistTitleProps {
  artistId: string;
}

const ArtistTitle = ({ artistId }: ArtistTitleProps) => {
  const {
    query: { data: artistProfile },
  } = useArtistProfile(artistId);

  return (
    <LText
      fontWeight={600}
      fontSize={16}
      numberOfLines={1}
      style={{ textAlign: Platform.OS === "web" ? "center" : undefined }}
    >
      {artistProfile?.name}
    </LText>
  );
};

const PlaylistTitleComp = ({ collectionId }: { collectionId: string }) => {
  const {
    query: { data: playlist },
  } = usePlaylist(collectionId);

  return (
    <LText
      fontWeight={600}
      fontSize={16}
      numberOfLines={1}
      style={{ textAlign: Platform.OS === "web" ? "center" : undefined }}
    >
      {playlist?.title}
    </LText>
  );
};

const PlaylistTitle = ({ collectionId }: { collectionId: string }) => {
  return <PlaylistTitleComp collectionId={collectionId} />;
};

const PersonalPlaylistTitleComp = ({
  collectionId,
}: {
  collectionId: string;
}) => {
  const {
    query: { data: playlist },
  } = usePlaylist(collectionId);

  return (
    <LText
      fontWeight={600}
      fontSize={16}
      numberOfLines={1}
      style={{ textAlign: Platform.OS === "web" ? "center" : undefined }}
    >
      {playlist?.title}
    </LText>
  );
};

const PersonalPlaylistTitle = ({ collectionId }: { collectionId: string }) => {
  return <PersonalPlaylistTitleComp collectionId={collectionId} />;
};
